import React, { useEffect, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.core.css';
import './ManageTermsAndConditions.scss';
import { Box, Skeleton } from '@chakra-ui/react';
import {
  getIsEditabledTermsAndConditionsEnabled,
  getIsTermsAndConditionsChanged,
  getStoreTermsAndConditions
} from '../ducks/selectors';
import { useDispatch } from 'react-redux';
import {
  callToGetStoreTermsAndConditions,
  setIsTermsAndCondtionChanged,
  setUpdatedStoreTermsAndCondition
} from '../ducks/slice';

const Link = Quill.import('formats/link');

class CustomLink extends Link {
  static create(value: any) {
    let href = '';
    
    // Handle different value types
    if (typeof value === 'string') {
      href = value;
    } else if (typeof value === 'object' && value !== null) {
      href = value.href || value.url || '';
    } else {
      href = String(value || '');
    }
    
    // Ensure href is a string and add protocol if needed
    if (typeof href === 'string' && href.length > 0) {
      if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('mailto:') && !href.startsWith('#')) {
        href = `https://${href}`;
      }
    }
    
    // Create the node with the processed href
    const node = super.create(href);
    
    // Set the href attribute
    if (href) {
      node.setAttribute('href', href);
    }
    
    // If value is an object, preserve all other attributes
    if (typeof value === 'object' && value !== null) {
      Object.keys(value).forEach(key => {
        if (key !== 'href' && key !== 'url') {
          node.setAttribute(key, String(value[key]));
        }
      });
    }
    
    return node;
  }

  static formats(node: HTMLElement) {
    const formats: any = {};
    
    // Preserve all attributes
    if (node.hasAttributes()) {
      const attrs = node.attributes;
      for (let i = 0; i < attrs.length; i++) {
        const attr = attrs[i];
        formats[attr.name] = attr.value;
      }
    }
    
    return formats;
  }

  static value(node: HTMLElement) {
    return node.getAttribute('href') || '';
  }
}

Quill.register(CustomLink, true);

const bulletSvg = `<path d="M4.375 7.5C5.41053 7.5 6.25 6.66053 6.25 5.625C6.25 4.58947 5.41053 3.75 4.375 3.75C3.33947 3.75 2.5 4.58947 2.5 5.625C2.5 6.66053 3.33947 7.5 4.375 7.5Z" fill="#212529"/>
<path d="M4.375 16.25C5.41053 16.25 6.25 15.4105 6.25 14.375C6.25 13.3395 5.41053 12.5 4.375 12.5C3.33947 12.5 2.5 13.3395 2.5 14.375C2.5 15.4105 3.33947 16.25 4.375 16.25Z" fill="#212529"/>
<path d="M10 13.75H18.75V15H10V13.75ZM10 5H18.75V6.25H10V5Z" fill="#212529"/>`;
const listsvg = `<path d="M10 13.75H18.75V15H10V13.75ZM10 5H18.75V6.25H10V5ZM5 7.5V2.5H3.75V3.125H2.5V4.375H3.75V7.5H2.5V8.75H6.25V7.5H5ZM6.25 17.5H2.5V15C2.5 14.6685 2.6317 14.3505 2.86612 14.1161C3.10054 13.8817 3.41848 13.75 3.75 13.75H5V12.5H2.5V11.25H5C5.33152 11.25 5.64946 11.3817 5.88388 11.6161C6.1183 11.8505 6.25 12.1685 6.25 12.5V13.75C6.25 14.0815 6.1183 14.3995 5.88388 14.6339C5.64946 14.8683 5.33152 15 5 15H3.75V16.25H6.25V17.5Z" fill="#212529"/>`;
const linkSvg = `<path d="M18.2803 4.22503C17.9319 3.87543 17.518 3.59804 17.0621 3.40877C16.6063 3.2195 16.1176 3.12207 15.6241 3.12207C15.1305 3.12207 14.6418 3.2195 14.186 3.40877C13.7302 3.59804 13.3162 3.87543 12.9678 4.22503L13.8553 5.11253C14.088 4.87984 14.3642 4.69526 14.6683 4.56934C14.9723 4.44341 15.2981 4.37859 15.6272 4.37859C15.9563 4.37859 16.2821 4.44341 16.5861 4.56934C16.8901 4.69526 17.1664 4.87984 17.3991 5.11253C17.6318 5.34521 17.8163 5.62145 17.9423 5.92547C18.0682 6.22949 18.133 6.55533 18.133 6.8844C18.133 7.21347 18.0682 7.53931 17.9423 7.84333C17.8163 8.14735 17.6318 8.42359 17.3991 8.65628L12.3991 13.6563C11.93 14.1262 11.2934 14.3905 10.6294 14.3911C9.9654 14.3917 9.32837 14.1285 8.85844 13.6594C8.38851 13.1903 8.12418 12.5537 8.12359 11.8897C8.123 11.2257 8.38621 10.5887 8.85532 10.1188L9.73657 9.23128L8.85532 8.34378L7.96782 9.23128C7.61822 9.57966 7.34083 9.99364 7.15156 10.4495C6.96229 10.9053 6.86486 11.394 6.86486 11.8875C6.86486 12.3811 6.96229 12.8698 7.15156 13.3256C7.34083 13.7814 7.61822 14.1954 7.96782 14.5438C8.675 15.2419 9.63036 15.6308 10.6241 15.625C11.1195 15.6271 11.6105 15.5309 12.0685 15.3421C12.5266 15.1533 12.9427 14.8756 13.2928 14.525L18.2928 9.52503C18.9934 8.82025 19.3856 7.86619 19.3833 6.87244C19.3809 5.87869 18.9842 4.9265 18.2803 4.22503Z" fill="#212529"/>
<path d="M2.61782 15.5125C2.38444 15.2802 2.19925 15.0041 2.07288 14.7C1.94652 14.396 1.88146 14.0699 1.88146 13.7407C1.88146 13.4114 1.94652 13.0853 2.07288 12.7813C2.19925 12.4772 2.38444 12.2011 2.61782 11.9688L7.61782 6.96878C7.85011 6.7354 8.12622 6.55021 8.4303 6.42384C8.73437 6.29748 9.06041 6.23243 9.38969 6.23243C9.71897 6.23243 10.045 6.29748 10.3491 6.42384C10.6532 6.55021 10.9293 6.7354 11.1616 6.96878C11.3935 7.2029 11.576 7.48119 11.6984 7.78716C11.8208 8.09313 11.8805 8.42055 11.8741 8.75003C11.876 9.08053 11.8123 9.40813 11.6868 9.71388C11.5613 10.0196 11.3764 10.2974 11.1428 10.5313L9.81782 11.875L10.7053 12.7625L12.0303 11.4375C12.7356 10.7322 13.1319 9.77561 13.1319 8.77815C13.1319 7.78069 12.7356 6.82409 12.0303 6.11878C11.325 5.41347 10.3684 5.01723 9.37094 5.01723C8.37348 5.01723 7.41688 5.41347 6.71157 6.11878L1.71157 11.1188C1.36103 11.4673 1.08284 11.8816 0.893018 12.338C0.703192 12.7944 0.605469 13.2839 0.605469 13.7782C0.605469 14.2725 0.703192 14.7619 0.893018 15.2183C1.08284 15.6747 1.36103 16.089 1.71157 16.4375C2.42333 17.1303 3.38088 17.5124 4.37407 17.5C5.376 17.501 6.33764 17.1055 7.04907 16.4L6.16157 15.5125C5.92927 15.7459 5.65316 15.9311 5.34909 16.0575C5.04501 16.1838 4.71897 16.2489 4.38969 16.2489C4.06041 16.2489 3.73437 16.1838 3.4303 16.0575C3.12622 15.9311 2.85011 15.7459 2.61782 15.5125Z" fill="#212529"/>`;

const downArrowSvg = `<path d="M8 10.9998L3 5.9998L3.7 5.2998L8 9.5998L12.3 5.2998L13 5.9998L8 10.9998Z" fill="#212529"/>`;
const formats = [
  'font',
  'size',
  'color',
  'align',
  'bold',
  'italic',
  'underline',
  'strike',
  'bullet',
  'list',
  'link'
];

const modules = {
  toolbar: [
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ color: [] }],
    [{ align: [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'bullet' }, { list: 'ordered' }],
    ['link']
  ]
};
const handleLinkClick = (event: MouseEvent) => {
  if ((event.target as HTMLElement).tagName === 'A') {
    event.preventDefault();
  }
};

interface props {
  setValue: React.Dispatch<React.SetStateAction<string>>
  value: string
}

function ManageTermsAndConditions({setValue, value}: props) {
  const storeTermsAndConditions = getStoreTermsAndConditions();
  const quillRef = useRef<ReactQuill>(null);
  const isTermsAndConditionsChanged = getIsTermsAndConditionsChanged();
  const isEditableTermsAndConditionsEnabled = getIsEditabledTermsAndConditionsEnabled();
  console.log({ storeTermsAndConditions });
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(callToGetStoreTermsAndConditions());
  }, []);

  useEffect(() => {
    setValue(storeTermsAndConditions.terms_and_conditions);
  }, [storeTermsAndConditions.terms_and_conditions]); 

  useEffect(() => {
    let qlBullet = document.querySelector('.ql-list[value="bullet"] svg');
    if (qlBullet) qlBullet.innerHTML = bulletSvg;
    let qlList = document.querySelector('.ql-list[value="ordered"] svg');
    if (qlList) qlList.innerHTML = listsvg;
    let qlLink = document.querySelector('.ql-link svg');
    if (qlLink) {
      qlLink.innerHTML = linkSvg;
      qlLink.setAttribute('viewBox', '0 0 20 20 ');
    }
    let qlDownArrowFont = document.querySelector('.ql-font .ql-picker-label svg');
    if (qlDownArrowFont) qlDownArrowFont.innerHTML = downArrowSvg;
    let qlDownArrowSize = document.querySelector('.ql-size .ql-picker-label svg');
    if (qlDownArrowSize) qlDownArrowSize.innerHTML = downArrowSvg;
  }, []);

  useEffect(() => {
    const quillInstance = quillRef.current?.getEditor();
    if (quillInstance) quillInstance.root.addEventListener('click', handleLinkClick);
    return () => {
      if (quillInstance) quillInstance.root.removeEventListener('click', handleLinkClick);
    };
  }, []);

  const isEditorEmpty = () => {
    const editor = quillRef.current?.getEditor();
    const text = editor?.getText().trim();
    return text === '';
  };

  const onChangeHandler = async (e: string) => {
    setValue(e);
    if (!isEditorEmpty()){
      if (storeTermsAndConditions.terms_and_conditions !== e) {
        dispatch(setUpdatedStoreTermsAndCondition({ terms_and_conditions: e }));
        dispatch(setIsTermsAndCondtionChanged(true));
      }
      else dispatch(setIsTermsAndCondtionChanged(false));
    } else dispatch(setIsTermsAndCondtionChanged(false));
  };
  return (
    <Box>
      <Box className="terms-and-condition__label">Terms & Conditions</Box>
      {!isEditableTermsAndConditionsEnabled ? (
        <>
          <Skeleton className='terms-and-conditions__skeleton' isLoaded={Boolean(storeTermsAndConditions.terms_and_conditions)}>
            <div
              className="terms-and-condition__non-editable-text view ql-editor"
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </Skeleton>
        </>
      ) : (
        <div className="custom-editor">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChangeHandler}
            formats={formats}
            modules={modules}
            placeholder="Write terms and conditions..."
          />
        </div>
      )}
    </Box>
  );
}

export default ManageTermsAndConditions;
